import { useEffect } from 'react';
import useCustomHook from '@/hooks/useCustomHook';
import { useGamificationContext } from '@/shared/Gamification/GamificationContext/GamificationContext';
import useMaximizeScreen from '@/shared/Activities/ActivityComponents/MaximizeScreen/useMaximizeScreen.hook';

export default function useStoryWithQuestions({
  activity,
  activityConf,
  pages,
  taskProgressActions,
  forceRestart,
  setCurrentPage,
}) {
  const gamificationContext = useGamificationContext();
  const { maximize, minimize } = useMaximizeScreen();

  const { state, actions } = useCustomHook({
    defaultState: {
      pagesLength: 0,
      questionsCount: 0,
      currentPage: null,
      lastProgress: 0,
      startLoading: false,
      savingProgress: false,
      visitedPages: [],
      currentPageFinished: false,
      progress: {
        activityType: 'storyWithQuestions',
        miniactivityType: activityConf.miniactivityType,
        progress: 0,
        ...(activityConf.correctness
          ? {
              correct: 0,
              answers: [],
            }
          : {}),
      },
      restartIndex: 0,
      finished: false,
      sentProgresses: [],
    },
    actions: {
      onActivityStart: async () => {
        if (taskProgressActions) {
          actions.setState({ startLoading: true });
          await taskProgressActions.createProgress({
            activityType: 'storyWithQuestions',
            miniactivityType: activityConf.miniactivityType,
            progress: 0,
          });
        }
        maximize();
        actions.setState({ startLoading: false, currentPage: pages[0] });
      },
      prevPage: () => {
        actions.setState({ currentPage: pages[state.currentPage.id - 2] });
      },
      nextPage: () => {
        actions.setState({
          currentPage: pages[state.currentPage.id],
          currentPageFinished: false,
        });
      },
      finish: async () => {
        gamificationContext.actions.putData({
          activityId: activity.id,
          miniactivityType: activityConf.miniactivityType,
          progress: 100,
          correctAnswers: [],
        });
        if (taskProgressActions) {
          actions.setState({ savingProgress: true });
          const progress = {
            ...state.progress,
            progress: 100,
          };
          if (activityConf.correctness) {
            progress.correct =
              state.progress.correct == 99 ? 100 : state.progress.correct;
          }
          await taskProgressActions.updateProgress(progress);
          actions.setState({
            savingProgress: false,
            progress,
            finished: true,
          });
        } else {
          minimize();
          actions.setState({ finished: true });
        }
      },
      onActivityRestart: async () => {
        taskProgressActions?.onActivityRestarted();
        actions.resetState({
          pagesLength: state.pagesLength,
          questionsCount: state.questionsCount,
          restartIndex: state.restartIndex + 1,
        });
      },
      shared: {
        onActivityProgress: async (pageId, pageAnswers = []) => {
          if (!state.sentProgresses.includes(pageId)) {
            const calculatedProgress = Math.min(
              99,
              Math.ceil((pageId / pages.length) * 100)
            );

            const progress = {
              ...state.progress,
              progress: calculatedProgress,
            };

            if (activityConf.correctness) {
              const answers = state.progress.answers.filter(
                (answer) => answer.pageId != pageId
              );
              answers.push(...pageAnswers);

              const correctness = calculateCorrectness(
                answers,
                state.questionsCount
              );

              progress.correct =
                correctness <= calculatedProgress
                  ? correctness
                  : calculatedProgress;
              progress.answers = answers;
            }

            actions.setState({
              savingProgress: true,
              sentProgresses: [...state.sentProgresses, pageId],
              visitedPages: [...state.visitedPages, pageId],
              currentPageFinished: true,
            });

            gamificationContext.actions.putData({
              activityId: activity.id,
              miniactivityType: activityConf.miniactivityType,
              progress: progress.progress,
              correctAnswers: pageAnswers
                .filter((answer) => answer.correct)
                .map((answer) => `${answer.pageId}-${answer.id}`),
            });
            if (taskProgressActions) {
              await taskProgressActions.updateProgress(progress);
            }
            actions.setState({
              savingProgress: false,
              progress,
            });
          }
        },
      },
    },
  });

  useEffect(() => {
    if (pages) {
      const questionsCount = pages.reduce(
        (acc, page) => acc + getQuestionsLengthByType(page),
        0
      );
      actions.setState({
        pagesLength: pages.length,
        questionsCount,
      });
    }
  }, [pages]);

  useEffect(() => {
    if (state.restartIndex) {
      actions.onActivityStart();
    }
  }, [state.restartIndex]);

  useEffect(() => {
    if (forceRestart) {
      actions.onActivityRestart();
    }
  }, [forceRestart]);

  useEffect(() => {
    if (state.pagesLength) {
      actions.onActivityStart();
    }
  }, [state.pagesLength]);

  useEffect(() => {
    setCurrentPage(state.currentPage);
    return () => setCurrentPage(null);
  }, [state.currentPage]);

  return {
    state: {
      ...state,
      isGamificationStudent: gamificationContext.state.isGamificationStudent,
    },
    actions,
  };
}

function calculateCorrectness(answers, questionsCount) {
  const correctAnswers = answers.filter((answer) => answer.correct);
  return answers.length
    ? Math.min(100, Math.ceil((correctAnswers.length / questionsCount) * 100))
    : 100;
}

function getQuestionsLengthByType(page) {
  return {
    text: 0,
    fillGaps: page.data.options?.length,
    drag: page.data.options?.length,
    options: page.data.multiple ? page.data.options?.length : 1,
  }[page.type];
}
